/**
 * Builds a URL query string based on a base URL and query parameters.
 *
 * @param {string} baseUrl - The base URL (e.g., 'https://example.com').
 * @param {object} queryParams - Query parameters as a JavaScript object.
 *                              For example: { param1: 'value1', param2: 'value2' }
 * @returns {string} - The complete URL with the reassembled query string.
 * @throws {TypeError} - If baseUrl is not a string or is empty.
 * @throws {TypeError} - If queryParams is not an object or is empty.
 */
export function buildUrlQuery(baseUrl = '', queryParams = {}) {
  // Validate that baseUrl is a string
  if (typeof baseUrl !== 'string') {
    throw new TypeError('baseUrl must be a string.');
  }

  // Validate that baseUrl is not empty
  if (baseUrl === '') {
    throw new TypeError('baseUrl cannot be empty.');
  }

  // Validate that baseUrl format
  if (/^https?:\/\/.*/.test(baseUrl) === false) {
    throw new TypeError('baseUrl format invalid need include http:// or https:// in string first.');
  }

  // Validate that queryParams is an object
  if (typeof queryParams !== 'object') {
    throw new TypeError('queryParams must be an object.');
  }

  // Validate that queryParams is not empty
  if (Object.keys(queryParams).length === 0) {
    return baseUrl;
  }

  const url = new URL(baseUrl);
  const params = url.searchParams;

  // Use Object.keys to iterate over the query parameters
  for (const k of Object.keys(queryParams)) {
    // Check if the query parameter already exists for the key
    if (params.has(k)) {
      // If it exists, update the value
      params.set(k, queryParams[k]);
    } else {
      // If it doesn't exist, append the new query parameter
      params.append(k, queryParams[k]);
    }
  }

  return url.href;
}
